"use client";

import { useId, useMemo } from "react";
import { cn } from "@/lib/utils";
import { SportsbookLogoMark } from "@/components/sportsbook-logos";
import { ScrollFade } from "@/components/odds-screen/live/top-menus";
import { pickerGroups } from "@/lib/book-catalog";
import { useBookCatalog } from "@/contexts/book-catalog-context";
import { useBookAvailability } from "@/contexts/book-availability-context";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";
import { useHeld } from "@/hooks/use-held";
import { useDfs } from "@/hooks/use-odds-live";
import { getFantasyLiveClient } from "@/lib/odds-screen/live/client";
import type { PlatformType } from "@/components/optimizer/utils-v2";
import type { Dfs, LeagueId } from "@/lib/odds-screen/live/types";
import { quietClass } from "./chrome";
import { BOOK_STRIP_ROW } from "./book-strip-styles";
import { appPlays, type AppPlays } from "./model";

const COUNTED = "+EV plays per app in these leagues at standard payouts, before your other filters";
const UNGRADED = "No breakeven for this app here, so the board doesn't grade its picks";
const NO_DFS: Dfs = new Map();

function playsLabel(name: string, plays: AppPlays | null, uncounted: string): string {
  if (plays === null) return `${name}, ${uncounted}`;
  if (plays === "ungraded") return `${name}, not graded here`;
  const counted = `${plays.count} ${plays.count === 1 ? "play" : "plays"}`;
  return plays.atLeast ? `${name}, at least ${counted}` : `${name}, ${counted}`;
}

function PlaysCount({ plays }: { plays: AppPlays | null }) {
  if (plays === null || plays === "ungraded") return <span className="data-num text-[13px] font-semibold text-zinc-600 md:text-[12px]">–</span>;
  return <span className={cn("data-num text-[13px] font-semibold md:text-[12px]", plays.count > 0 ? "text-accent-value" : "text-zinc-500")}>{`${plays.count}${plays.atLeast ? "+" : ""}`}</span>;
}

export function BookStrip({ books, leagues, needOf, weighted, book, heldAtMs, onPick }: {
  books: readonly PlatformType[];
  leagues: readonly LeagueId[];
  needOf: (app: PlatformType, league: LeagueId) => number | null;
  weighted: boolean;
  book: PlatformType | null;
  heldAtMs: number | null;
  onPick: (app: PlatformType) => void;
}) {
  const catalog = useBookCatalog();
  const { isHere } = useBookAvailability();
  const captionId = useId();
  const streamed = useDfs(getFantasyLiveClient());
  const { value: dfs } = useHeld(streamed, heldAtMs === null, String(heldAtMs), streamed.value !== null);
  const apps = useMemo(() => pickerGroups(catalog, books).flatMap((group) => group.books).filter(isHere), [catalog, books, isHere]);
  const plays = useMemo(() => (weighted ? null : appPlays(dfs.value ?? NO_DFS, leagues, apps, needOf)), [weighted, dfs.value, leagues, apps, needOf]);
  if (apps.length === 0) return null;
  const uncounted = dfs.value !== null || dfs.phase === "ended" ? "nothing posted" : "loading…";
  return (
    <ScrollFade className={BOOK_STRIP_ROW}>
      <span id={captionId} title={plays === null ? undefined : COUNTED} className="flex-shrink-0 pr-1 text-[11px] font-medium text-zinc-500">{plays === null ? "Counts hidden while weights are on" : "Plays"}</span>
      {apps.map((app) => {
        const name = sportsbookIdToDisplay(app);
        const counted = plays?.get(app) ?? null;
        const selected = app === book;
        return (
          <button
            key={app}
            type="button"
            aria-pressed={selected}
            aria-label={plays === null ? name : playsLabel(name, counted, uncounted)}
            aria-describedby={plays === null ? captionId : undefined}
            title={counted === null ? undefined : counted === "ungraded" ? UNGRADED : COUNTED}
            onClick={selected ? undefined : () => onPick(app)}
            className={cn(quietClass(selected, true), "md:h-7")}
          >
            <SportsbookLogoMark sportsbook={app} size={16} decorative />
            {plays !== null && <PlaysCount plays={counted} />}
          </button>
        );
      })}
    </ScrollFade>
  );
}
